/**
 * Nearby browse (no q) — one sales-point directory row (mockup MOCKUP-NEARBY-CARDS).
 * Category · distance · walk time; star AND chevron BOTH; hours NON-AUTHORITY until public DTO + wire (3c).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  normalizeLogoChipBackgroundSettings,
  normalizeLogoChipRimSettings,
} from 'pi-kiosk-shared';
import { DirectoryEntityRow } from '../../shared/ui/DirectoryEntityRow.js';
import { FavoriteStarIcon } from '../../shared/ui/FavoriteStarIcon.js';
import { useStoreDirectoryThumb } from '../../shared/ui/useDirectoryEntityThumb.js';
import { DIRECTORY_NEARBY_INSET_ROW_CLASS } from '../../shared/ui/directoryPickerLayout.js';
import type { FormatDistanceMetersLabels } from '../../shared/ui/directoryListFilter.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import {
  isPendingFavoriteSalesPointId,
  removePendingFavoriteSpContact,
  stagePendingFavoriteSpContact,
} from '../account/pendingFavoriteSpContacts.js';
import { setSalesPointFavorite } from '../account/salesPointContactsApi.js';
import { useSalesPointContactsByToken } from '../account/useSalesPointContactsByToken.js';
import {
  PLATFORM_DIRECTORY_SHOPS_TENANT,
  readDirectoryShopsPersist,
} from '../../shared/session/searchPersistStorage.js';
import { invalidatePublicCustomerSalesPointsCache } from '../../shared/api/customerDirectoryCacheInvalidate.js';
import { showToast } from '../../shared/ui/toastStore.js';
import {
  directoryThumbFetchPriority,
  directoryThumbLoading,
} from '../../shared/ui/directoryThumbLoading.js';
import { resolveDirectoryShopOpenAction } from '../shops/resolveDirectoryShopOpenAction.js';
import { resolveDiscoveryOfferDisplayImageUrl } from './browseOfferPresentation.js';
import {
  buildNearbySalesPointMetaParts,
  resolveNearbySalesPointOpenHoursLabel,
} from './browseNearbySalesPointPresentation.js';
import type { DiscoveryIntentSalesPointGroup } from './groupDiscoveryOffersBySalesPoint.js';

export type BrowseNearbySalesPointCardProps = {
  readonly group: DiscoveryIntentSalesPointGroup;
  readonly index: number;
  readonly distanceFormatter: FormatDistanceMetersLabels;
};

export function BrowseNearbySalesPointCard(props: BrowseNearbySalesPointCardProps): JSX.Element {
  const { group, index, distanceFormatter } = props;
  const { t } = useTranslation('customer');
  const { apiBaseUrl } = useRuntimeConfig();
  const { session } = useCustomerSession();
  const queryClient = useQueryClient();
  const signedIn = isGloballySignedInChrome({ session });
  const favoritesQuery = useSalesPointContactsByToken(signedIn);
  const [favoritePending, setFavoritePending] = useState(false);
  const [guestFavoriteEpoch, setGuestFavoriteEpoch] = useState(0);

  const headOffer = group.offers[0];
  const displayName = group.salesPointName.trim().length > 0
    ? group.salesPointName.trim()
    : group.salesPointCode;

  const favoriteIds = useMemo(
    () => new Set((favoritesQuery.data?.items ?? []).map((fav) => fav.salesPointId)),
    [favoritesQuery.data?.items],
  );
  const isFavorite = signedIn
    ? favoriteIds.has(group.salesPointId)
    : (void guestFavoriteEpoch, isPendingFavoriteSalesPointId(group.salesPointId));

  const walkTimeLabels = useMemo(
    () => ({
      minutes: t('browse.discovery.nearbyWalkMinutes'),
    }),
    [t],
  );

  const metaParts = useMemo(
    () => buildNearbySalesPointMetaParts(group, distanceFormatter, walkTimeLabels),
    [distanceFormatter, group, walkTimeLabels],
  );
  // 3c hours NON-AUTHORITY until public nearby/SP DTO + wire; never invent from description/clocks.
  // (H09-UI/TECH is seller LEGAL — orthogonal; do not conflate.)
  const openHoursLabel = resolveNearbySalesPointOpenHoursLabel(group);

  const displayImageUrl =
    headOffer !== undefined ? resolveDiscoveryOfferDisplayImageUrl(headOffer) : null;

  const { thumbProps } = useStoreDirectoryThumb({
    imageUrl: displayImageUrl,
    imageUrlDark: null,
    tenantLogoUrl: null,
    tenantLogoUrlDark: null,
    monogram: {
      id: group.salesPointId,
      displayName,
      code: group.salesPointCode,
    },
    showTenantChip: false,
    // D23 — compact Home nearby thumb band 52–60 (md token).
    size: 'md',
    loading: directoryThumbLoading(index),
    fetchPriority: directoryThumbFetchPriority(index),
    testId: `customer-browse-nearby-sp-thumb-${group.salesPointId}`,
  });

  const rowPressHref = useMemo(
    () =>
      resolveDirectoryShopOpenAction({
        customerShopUrl: group.customerShopUrl,
        targetTenantCode: group.tenantCode,
      })?.url,
    [group.customerShopUrl, group.tenantCode],
  );

  const handleUnfavoriteGuest = (): void => {
    removePendingFavoriteSpContact(group.salesPointId);
    setGuestFavoriteEpoch((n) => n + 1);
  };

  const handleFavorite = async (): Promise<void> => {
    if (isFavorite || headOffer === undefined) {
      return;
    }
    if (!signedIn) {
      // Discovery offers omit SP branding on wire — stage normalized defaults only;
      // G1 mergePendingFavoriteBrandingFromLive repairs from public directory after SSE.
      stagePendingFavoriteSpContact({
        tenantCode: group.tenantCode,
        salesPointId: group.salesPointId,
        salesPointSlug: group.salesPointCode,
        code: group.salesPointCode,
        name: group.salesPointName,
        nameLocales: null,
        location: null,
        tenantId: group.tenantId,
        tenantName: group.tenantName,
        customerShopUrl: group.customerShopUrl,
        imageUrl: displayImageUrl,
        imageUrlDark: null,
        tenantLogoUrl: null,
        tenantLogoUrlDark: null,
        ...normalizeLogoChipRimSettings({}),
        ...normalizeLogoChipBackgroundSettings({}),
      });
      setGuestFavoriteEpoch((n) => n + 1);
      return;
    }
    setFavoritePending(true);
    try {
      await setSalesPointFavorite({
        apiBaseUrl,
        body: { salesPointId: group.salesPointId, favorite: true },
      });
      const customerKey = session?.customerId ?? 'bearer';
      await queryClient.invalidateQueries({
        queryKey: customerQueryKeys.salesPointContacts(customerKey),
      });
      if (readDirectoryShopsPersist(PLATFORM_DIRECTORY_SHOPS_TENANT)?.sort === 'favoriteDesc') {
        invalidatePublicCustomerSalesPointsCache(queryClient, apiBaseUrl);
      }
    } catch {
      showToast({
        message: t('errors.spContact.recordTransport'),
        variant: 'danger',
      });
    } finally {
      setFavoritePending(false);
    }
  };

  const metaLine = metaParts.join(' · ');
  const subtitle =
    metaLine.length > 0 || openHoursLabel !== null ? (
      <>
        {metaLine.length > 0 ? (
          <span data-testid={`customer-browse-nearby-sp-meta-${group.salesPointId}`}>
            {metaLine}
          </span>
        ) : null}
        {openHoursLabel !== null ? (
          <span
            className="mt-0.5 block text-[var(--color-success)]"
            data-testid={`customer-browse-nearby-sp-hours-${group.salesPointId}`}
          >
            {openHoursLabel}
          </span>
        ) : null}
      </>
    ) : undefined;

  return (
    <li
      className={DIRECTORY_NEARBY_INSET_ROW_CLASS}
      data-testid={`customer-browse-nearby-sp-${group.salesPointId}`}
    >
      <DirectoryEntityRow
        thumb={thumbProps}
        title={displayName}
        subtitle={subtitle}
        rowPressHref={rowPressHref}
        rowPressDisabled={rowPressHref === undefined}
        rowPressTestId={`customer-browse-nearby-sp-open-${group.salesPointId}`}
        showChevron
        density="compact"
        layout="inline"
        testId={`customer-browse-nearby-sp-row-${group.salesPointId}`}
        trailing={
          <button
            type="button"
            className={[
              'inline-flex h-11 w-11 min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full border border-transparent bg-transparent',
              'hover:bg-[var(--color-surface-muted)]',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]',
              'disabled:cursor-not-allowed disabled:opacity-[0.55]',
              isFavorite
                ? 'text-[var(--color-warning)]'
                : 'text-[var(--color-on-surface-muted)]',
            ].join(' ')}
            data-testid={`customer-browse-nearby-sp-star-${group.salesPointId}`}
            title={isFavorite ? t('account.shop.unfavorite') : t('account.shop.favorite')}
            aria-label={isFavorite ? t('account.shop.unfavorite') : t('account.shop.favorite')}
            aria-pressed={isFavorite}
            disabled={favoritePending}
            onClick={(event) => {
              event.stopPropagation();
              if (isFavorite && !signedIn) {
                handleUnfavoriteGuest();
                return;
              }
              void handleFavorite();
            }}
          >
            <FavoriteStarIcon filled={isFavorite} />
          </button>
        }
      />
    </li>
  );
}
